// Fretboard classes for JamCam (browser version)
// Migrated and adapted from guitar-normal-guy classes

import { Point, DrawSettings, FretNote } from './core/types';
import {
  computeCentroid,
  computeLinearRegression,
  perpendicularDistance,
} from './utils/math';
import { douglasPeucker, lineSegmentIntersection } from './roboflowOverlay';

// Segmentation class for handling polygon-based detections
export class Segmentation {
  points: Point[];
  dpEpsilon: number;
  centroid: Point;
  dpPoints: Point[];

  constructor(points: Point[], dpEpsilon: number = 12) {
    this.points = points;
    this.dpEpsilon = dpEpsilon;
    this.centroid = computeCentroid(points);
    this.dpPoints = douglasPeucker(points, dpEpsilon);
  }

  calculateDouglasPeucker(epsilon: number = this.dpEpsilon): Point[] {
    if (epsilon === this.dpEpsilon) return this.dpPoints;
    return douglasPeucker(this.points, epsilon);
  }

  drawBorder(ctx: CanvasRenderingContext2D, settings: DrawSettings = {}) {
    if (this.points.length < 2) return;

    ctx.save();
    ctx.strokeStyle = settings.color || 'lime';
    ctx.lineWidth = settings.thickness || 1;
    ctx.globalAlpha = settings.opacity || 0.5;

    if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
    else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
    else ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(this.points[0].x, this.points[0].y);
    for (let i = 1; i < this.points.length; i++) {
      ctx.lineTo(this.points[i].x, this.points[i].y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }

  drawFill(ctx: CanvasRenderingContext2D, settings: DrawSettings = {}) {
    if (this.points.length < 3) return; // Need at least 3 points for a fill

    ctx.save();
    ctx.fillStyle = settings.color || 'rgba(0,0,255,0.3)';
    ctx.globalAlpha = settings.opacity || 0.3;

    ctx.beginPath();
    ctx.moveTo(this.points[0].x, this.points[0].y);
    for (let i = 1; i < this.points.length; i++) {
      ctx.lineTo(this.points[i].x, this.points[i].y);
    }
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }

  drawCentroid(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & { radius?: number } = {}
  ) {
    ctx.save();
    ctx.fillStyle = settings.color || 'rgba(0,0,255,0.5)';
    ctx.globalAlpha = settings.opacity || 1;
    ctx.beginPath();
    ctx.arc(
      this.centroid.x,
      this.centroid.y,
      settings.radius || 4,
      0,
      2 * Math.PI
    );
    ctx.fill();
    ctx.restore();
  }

  drawDouglasPeucker(
    ctx: CanvasRenderingContext2D,
    epsilon: number,
    settings: DrawSettings = {}
  ) {
    const dpPoints = this.calculateDouglasPeucker(epsilon);
    if (dpPoints.length < 2) return;

    ctx.save();
    ctx.strokeStyle = settings.color || 'yellow';
    ctx.lineWidth = settings.thickness || 3;
    ctx.globalAlpha = settings.opacity || 0.1;

    if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
    else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
    else ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(dpPoints[0].x, dpPoints[0].y);
    for (let i = 1; i < dpPoints.length; i++) {
      ctx.lineTo(dpPoints[i].x, dpPoints[i].y);
    }
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
  }
}

export class Fret {
  points: Point[];
  centroid: Point;
  confidence: number;
  class: string;
  fretNotes: Array<{ string: number; x: number | null; y: number | null }> = [];
  dpEpsilon: number;
  dpPoints: Point[];
  isEstimated: boolean = false; // New property to track estimated frets

  constructor(prediction: any, isEstimated: boolean = false) {
    this.points = prediction.points;
    this.confidence = prediction.confidence;
    this.class = prediction.class;
    this.dpEpsilon = 12;
    this.centroid = computeCentroid(this.points);
    this.dpPoints = douglasPeucker(this.points, this.dpEpsilon);
    this.isEstimated = isEstimated;
  }

  calculateDouglasPeucker(epsilon: number = this.dpEpsilon): Point[] {
    if (epsilon === this.dpEpsilon) return this.dpPoints;
    return douglasPeucker(this.points, epsilon);
  }

  drawFretNotes(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & { diameter?: number } = {}
  ) {
    if (!this.fretNotes || this.fretNotes.length === 0) return;
    ctx.save();
    ctx.fillStyle = settings.color || 'white';
    ctx.globalAlpha = settings.opacity || 1;
    const radius = (settings.diameter || 8) / 2;
    this.fretNotes.forEach((note) => {
      if (note.x !== null && note.y !== null) {
        ctx.beginPath();
        ctx.arc(note.x, note.y, radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
    ctx.restore();
  }
}

export class GuitarString {
  slope: number;
  intercept: number;
  index: number;
  ghostNotes: Point[] = [];
  estimatedGhostNotes: Point[] = []; // New property for estimated notes

  constructor(slope: number, intercept: number, index: number) {
    this.slope = slope;
    this.intercept = intercept;
    this.index = index;
  }

  // Original method from guitar-normal-guy using line-segment intersections
  calculateGhostNoteForFret(fretDpPoints: Point[]): Point | null {
    let intersections: Point[] = [];
    const p0 = { x: 0, y: this.intercept };
    const d = { x: 1, y: this.slope };

    for (let i = 0; i < fretDpPoints.length; i++) {
      const a = fretDpPoints[i];
      const b = fretDpPoints[(i + 1) % fretDpPoints.length];
      const inter = lineSegmentIntersection(p0, d, a, b);
      if (inter) intersections.push(inter);
    }

    if (intersections.length === 0) return null;
    // Sort by x coordinate and take the leftmost (closest to bridge)
    intersections.sort((p1, p2) => p1.x - p2.x);
    return intersections[0];
  }

  calculateGhostNoteLocations(fretObjects: Fret[]): Point[] {
    this.ghostNotes = [];

    fretObjects.forEach((fret) => {
      const dp = fret.calculateDouglasPeucker(12);
      const ghost = this.calculateGhostNoteForFret(dp);
      this.ghostNotes.push(ghost || { x: 0, y: 0 }); // Push null-safe point

      // Also add to fret's fretNotes array (like original)
      fret.fretNotes.push({
        string: this.index,
        x: ghost ? ghost.x : null,
        y: ghost ? ghost.y : null,
      });
    });

    return this.ghostNotes;
  }

  /**
   * Calculate ghost note locations for both detected and estimated frets
   */
  calculateGhostNoteLocationsWithEstimated(
    detectedFrets: Fret[],
    estimatedFrets: Fret[]
  ): { detected: Point[]; estimated: Point[] } {
    // Calculate detected notes
    this.ghostNotes = [];
    detectedFrets.forEach((fret) => {
      const dp = fret.calculateDouglasPeucker(12);
      const ghost = this.calculateGhostNoteForFret(dp);
      this.ghostNotes.push(ghost || { x: 0, y: 0 });

      fret.fretNotes.push({
        string: this.index,
        x: ghost ? ghost.x : null,
        y: ghost ? ghost.y : null,
      });
    });

    // Calculate estimated notes
    this.estimatedGhostNotes = [];
    estimatedFrets.forEach((fret) => {
      const dp = fret.calculateDouglasPeucker(12);
      const ghost = this.calculateGhostNoteForFret(dp);
      this.estimatedGhostNotes.push(ghost || { x: 0, y: 0 });

      fret.fretNotes.push({
        string: this.index,
        x: ghost ? ghost.x : null,
        y: ghost ? ghost.y : null,
      });
    });

    return {
      detected: this.ghostNotes,
      estimated: this.estimatedGhostNotes,
    };
  }

  // Static method to calculate aligned ghost notes for all strings using middle strings as reference
  static calculateAlignedGhostNoteLocations(
    fretObjects: Fret[],
    guitarStrings: GuitarString[]
  ) {
    const numStrings = guitarStrings.length;
    const middleString1Index = Math.floor((numStrings - 1) / 2); // e.g., string 2 for 6 strings (0-indexed)
    const middleString2Index = Math.ceil((numStrings - 1) / 2); // e.g., string 3 for 6 strings (0-indexed)

    // Clear all existing ghost notes
    guitarStrings.forEach((gs) => {
      gs.ghostNotes = [];
    });

    // Clear existing fret notes
    fretObjects.forEach((fret) => {
      fret.fretNotes = [];
    });

    fretObjects.forEach((fret, fretIndex) => {
      // Calculate intersections for the two middle strings using the original method
      const middleString1 = guitarStrings[middleString1Index];
      const middleString2 = guitarStrings[middleString2Index];

      const dp = fret.calculateDouglasPeucker(12);
      const ghost1 = middleString1.calculateGhostNoteForFret(dp);
      const ghost2 = middleString2.calculateGhostNoteForFret(dp);

      if (!ghost1 || !ghost2) {
        // Fallback to original method if middle strings don't intersect
        guitarStrings.forEach((gs) => {
          const ghost = gs.calculateGhostNoteForFret(dp);
          gs.ghostNotes.push(ghost || { x: 0, y: 0 });
          fret.fretNotes.push({
            string: gs.index,
            x: ghost ? ghost.x : null,
            y: ghost ? ghost.y : null,
          });
        });
        return;
      }

      // Create a line connecting the two middle points
      const dx = ghost2.x - ghost1.x;
      const dy = ghost2.y - ghost1.y;
      const fretLineSlope = dx === 0 ? Number.MAX_VALUE : dy / dx;
      const fretLineIntercept = ghost1.y - fretLineSlope * ghost1.x;

      // Check if we have a vertical fret line (infinite slope)
      const isVerticalFret =
        Math.abs(fretLineSlope) > 1000 || !isFinite(fretLineSlope);

      // For each string, find where the fret line intersects the string line
      guitarStrings.forEach((gs, stringIndex) => {
        if (stringIndex === middleString1Index) {
          // Use the calculated middle point
          gs.ghostNotes.push(ghost1);
          fret.fretNotes.push({
            string: gs.index,
            x: ghost1.x,
            y: ghost1.y,
          });
        } else if (stringIndex === middleString2Index) {
          // Use the calculated middle point
          gs.ghostNotes.push(ghost2);
          fret.fretNotes.push({
            string: gs.index,
            x: ghost2.x,
            y: ghost2.y,
          });
        } else {
          if (isVerticalFret) {
            // For vertical fret lines, use the average X coordinate and calculate Y from string line
            const avgX = (ghost1.x + ghost2.x) / 2;
            const y = gs.slope * avgX + gs.intercept;
            gs.ghostNotes.push({ x: avgX, y });
            fret.fretNotes.push({
              string: gs.index,
              x: avgX,
              y,
            });
          } else {
            // Calculate intersection of fret line with this string line
            const denominator = gs.slope - fretLineSlope;

            if (Math.abs(denominator) > 0.0001) {
              const x = (fretLineIntercept - gs.intercept) / denominator;
              const y = gs.slope * x + gs.intercept;
              gs.ghostNotes.push({ x, y });
              fret.fretNotes.push({
                string: gs.index,
                x,
                y,
              });
            } else {
              // Lines are parallel, fallback to original method
              const ghost = gs.calculateGhostNoteForFret(dp);
              gs.ghostNotes.push(ghost || { x: 0, y: 0 });
              fret.fretNotes.push({
                string: gs.index,
                x: ghost ? ghost.x : null,
                y: ghost ? ghost.y : null,
              });
            }
          }
        }
      });
    });
  }

  draw(ctx: CanvasRenderingContext2D, settings: DrawSettings = {}) {
    // Draw the string line (simplified - just draw across canvas width)
    ctx.save();
    ctx.strokeStyle = settings.color || 'blue';
    ctx.lineWidth = settings.thickness || 1;
    ctx.globalAlpha = settings.opacity || 0.1;

    if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
    else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
    else ctx.setLineDash([]);

    ctx.beginPath();
    ctx.moveTo(0, this.intercept);
    ctx.lineTo(
      ctx.canvas.width,
      this.slope * ctx.canvas.width + this.intercept
    );
    ctx.stroke();
    ctx.restore();
  }

  drawGhostNotes(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & { diameter?: number } = {}
  ) {
    if (!this.ghostNotes || this.ghostNotes.length === 0) return;
    ctx.save();
    ctx.fillStyle = settings.color || 'white';
    ctx.globalAlpha = settings.opacity || 1;
    const radius = (settings.diameter || 8) / 2;

    this.ghostNotes.forEach((pt) => {
      if (
        pt &&
        typeof pt.x === 'number' &&
        typeof pt.y === 'number' &&
        !isNaN(pt.x) &&
        !isNaN(pt.y)
      ) {
        // Draw all valid numeric points (including extrapolated ones)
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, 2 * Math.PI);
        ctx.fill();
      }
    });
    ctx.restore();
  }

  /**
   * Draw estimated ghost notes with different styling
   */
  drawEstimatedGhostNotes(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & { diameter?: number } = {}
  ) {
    if (!this.estimatedGhostNotes || this.estimatedGhostNotes.length === 0)
      return;
    ctx.save();
    ctx.fillStyle = settings.color || 'rgba(255,255,0,0.5)';
    ctx.globalAlpha = settings.opacity || 0.8;
    const radius = (settings.diameter || 6) / 2;

    // Draw border if specified
    if (settings.style === 'dashed') {
      ctx.setLineDash([3, 3]);
    }

    this.estimatedGhostNotes.forEach((pt) => {
      if (
        pt &&
        typeof pt.x === 'number' &&
        typeof pt.y === 'number' &&
        !isNaN(pt.x) &&
        !isNaN(pt.y)
      ) {
        ctx.beginPath();
        ctx.arc(pt.x, pt.y, radius, 0, 2 * Math.PI);
        ctx.fill();

        // Draw border if enabled
        if (settings.thickness && settings.thickness > 0) {
          ctx.strokeStyle = settings.color || 'rgba(255,255,0,0.8)';
          ctx.lineWidth = settings.thickness;
          ctx.stroke();
        }
      }
    });
    ctx.restore();
  }
}

export class Fretboard {
  frets: Fret[];
  board: any;
  topLineSlope: number = 0;
  topLineIntercept: number = 0;
  bottomLineSlope: number = 0;
  bottomLineIntercept: number = 0;
  fretBoardLength: number = 0;

  constructor(fretPredictions: any[], boardPrediction: any = null) {
    this.frets = fretPredictions.map((pred) => new Fret(pred));
    this.board = boardPrediction;
  }

  calculateTopLineSlope() {
    // Use the top points of frets to calculate top line
    const topPoints = this.frets.map((fret) => {
      const topY = Math.min(...fret.points.map((p) => p.y));
      const topPoint = fret.points.find((p) => p.y === topY) || fret.centroid;
      return topPoint;
    });

    if (topPoints.length >= 2) {
      const regression = computeLinearRegression(topPoints);
      this.topLineSlope = regression.m;
      this.topLineIntercept = regression.b;
    }
  }

  calculateBottomLineSlope() {
    // Use the bottom points of frets to calculate bottom line
    const bottomPoints = this.frets.map((fret) => {
      const bottomY = Math.max(...fret.points.map((p) => p.y));
      const bottomPoint =
        fret.points.find((p) => p.y === bottomY) || fret.centroid;
      return bottomPoint;
    });

    if (bottomPoints.length >= 2) {
      const regression = computeLinearRegression(bottomPoints);
      this.bottomLineSlope = regression.m;
      this.bottomLineIntercept = regression.b;
    }
  }

  drawTopLine(ctx: CanvasRenderingContext2D, settings: DrawSettings = {}) {
    ctx.save();
    ctx.strokeStyle = settings.color || 'black';
    ctx.lineWidth = settings.thickness || 2;
    ctx.globalAlpha = settings.opacity || 1;
    ctx.beginPath();
    ctx.moveTo(0, this.topLineIntercept);
    ctx.lineTo(
      ctx.canvas.width,
      this.topLineSlope * ctx.canvas.width + this.topLineIntercept
    );
    ctx.stroke();
    ctx.restore();
  }

  drawBottomLine(ctx: CanvasRenderingContext2D, settings: DrawSettings = {}) {
    ctx.save();
    ctx.strokeStyle = settings.color || 'black';
    ctx.lineWidth = settings.thickness || 2;
    ctx.globalAlpha = settings.opacity || 1;
    ctx.beginPath();
    ctx.moveTo(0, this.bottomLineIntercept);
    ctx.lineTo(
      ctx.canvas.width,
      this.bottomLineSlope * ctx.canvas.width + this.bottomLineIntercept
    );
    ctx.stroke();
    ctx.restore();
  }

  calculateSimpleStringLines(numStrings: number) {
    const stringLines = [];
    for (let i = 0; i < numStrings; i++) {
      const ratio = i / (numStrings - 1);
      const slope =
        this.topLineSlope + ratio * (this.bottomLineSlope - this.topLineSlope);
      const intercept =
        this.topLineIntercept +
        ratio * (this.bottomLineIntercept - this.topLineIntercept);
      stringLines.push({ slope, intercept });
    }
    return stringLines;
  }

  drawSimpleStringLines(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & { numStrings?: number } = {}
  ) {
    const numStrings = settings.numStrings || 6;
    const stringLines = this.calculateSimpleStringLines(numStrings);

    stringLines.forEach((line, index) => {
      ctx.save();
      ctx.strokeStyle = settings.color || 'blue';
      ctx.lineWidth = settings.thickness || 1;
      ctx.globalAlpha = settings.opacity || 0.1;

      if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
      else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
      else ctx.setLineDash([]);

      ctx.beginPath();
      ctx.moveTo(0, line.intercept);
      ctx.lineTo(
        ctx.canvas.width,
        line.slope * ctx.canvas.width + line.intercept
      );
      ctx.stroke();
      ctx.restore();
    });
  }

  // Calculate perpendicular line through a fret centroid
  calculatePerpendicularLine(fret: Fret) {
    const centerX = fret.centroid.x;
    const centerY = fret.centroid.y;

    // Calculate average slope of top and bottom lines
    const avgSlope = (this.topLineSlope + this.bottomLineSlope) / 2;

    // Perpendicular slope is negative reciprocal
    const perpSlope = avgSlope === 0 ? Number.MAX_VALUE : -1 / avgSlope;

    // Calculate intercept: y = mx + b -> b = y - mx
    const perpIntercept = centerY - perpSlope * centerX;

    return { slope: perpSlope, intercept: perpIntercept, centerX, centerY };
  }

  // Draw perpendicular fret lines
  drawPerpendicularFretLines(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & {
      length?: number;
      confidenceThreshold?: number;
    } = {}
  ) {
    const length = settings.length || 300;
    const threshold = settings.confidenceThreshold || 0.3;

    this.frets
      .filter((fret) => fret.confidence >= threshold)
      .forEach((fret) => {
        const perpLine = this.calculatePerpendicularLine(fret);
        const centerX = perpLine.centerX;
        const centerY = perpLine.centerY;

        // Calculate start and end points of perpendicular line
        const halfLength = length / 2;
        let startX, startY, endX, endY;

        if (Math.abs(perpLine.slope) > 1000) {
          // Nearly vertical line
          startX = centerX;
          startY = centerY - halfLength;
          endX = centerX;
          endY = centerY + halfLength;
        } else {
          // Calculate points along the perpendicular line
          const dx =
            halfLength / Math.sqrt(1 + perpLine.slope * perpLine.slope);
          startX = centerX - dx;
          startY = perpLine.slope * startX + perpLine.intercept;
          endX = centerX + dx;
          endY = perpLine.slope * endX + perpLine.intercept;
        }

        ctx.save();
        ctx.strokeStyle = settings.color || 'white';
        ctx.lineWidth = settings.thickness || 1;
        ctx.globalAlpha = settings.opacity || 0.2;

        if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
        else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
        else ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.restore();
      });
  }

  // Calculate string intersections with perpendicular fret lines
  calculateStringPerpendicularIntersections(numStrings: number = 6) {
    const stringLines = this.calculateSimpleStringLines(numStrings);
    const intersections: {
      fretIndex: number;
      stringIndex: number;
      point: Point;
    }[] = [];

    this.frets.forEach((fret, fretIndex) => {
      const perpLine = this.calculatePerpendicularLine(fret);

      stringLines.forEach((stringLine, stringIndex) => {
        // Calculate intersection of string line and perpendicular fret line
        const denominator = stringLine.slope - perpLine.slope;

        if (Math.abs(denominator) > 0.0001) {
          // Avoid division by zero
          const x = (perpLine.intercept - stringLine.intercept) / denominator;
          const y = stringLine.slope * x + stringLine.intercept;

          intersections.push({
            fretIndex,
            stringIndex,
            point: { x, y },
          });
        }
      });
    });

    return intersections;
  }

  /**
   * Estimate missing frets using logarithmic spacing
   * Guitar frets follow the formula: distance = scale_length * (1 - (1/2)^(fret_number/12))
   */
  estimateMissingFrets(maxFretsToEstimate: number = 5): Fret[] {
    if (this.frets.length < 2) {
      console.warn('Need at least 2 detected frets for gap filling');
      return [];
    }

    // Sort frets by x-coordinate (assuming horizontal guitar orientation)
    const sortedFrets = [...this.frets].sort(
      (a, b) => a.centroid.x - b.centroid.x
    );

    // Estimate scale length and nut position from detected frets
    const { scaleLength, nutX, nutY } =
      this.estimateScaleLengthAndNut(sortedFrets);

    console.log(
      `Gap filling: Estimated scale length=${scaleLength}, nut position=(${nutX}, ${nutY})`
    );

    if (!scaleLength || scaleLength <= 0) {
      console.warn('Could not estimate scale length for gap filling');
      return [];
    }

    // Generate theoretical fret positions
    const estimatedFrets: Fret[] = [];
    const existingFretPositions = new Set(
      sortedFrets.map((f) => Math.round(f.centroid.x))
    );

    for (let fretNum = 1; fretNum <= maxFretsToEstimate; fretNum++) {
      // Calculate theoretical position using logarithmic formula
      const theoreticalDistance =
        scaleLength * (1 - Math.pow(1 / 2, fretNum / 12));
      const theoreticalX = nutX + theoreticalDistance;
      const theoreticalY = nutY; // Use nut Y as baseline

      // Skip if we already have a fret near this position (within 20 pixels)
      const hasNearbyFret = sortedFrets.some(
        (f) => Math.abs(f.centroid.x - theoreticalX) < 20
      );

      if (!hasNearbyFret) {
        // Create estimated fret with simple rectangular points around the theoretical position
        const fretHeight =
          Math.abs(this.bottomLineIntercept - this.topLineIntercept) || 100;
        const fretWidth = 10; // Small width for the estimated fret

        console.log(
          `Gap filling: Creating estimated fret ${fretNum} at position (${theoreticalX}, ${theoreticalY})`
        );

        const estimatedFret = new Fret(
          {
            points: [
              {
                x: theoreticalX - fretWidth / 2,
                y: theoreticalY - fretHeight / 2,
              },
              {
                x: theoreticalX + fretWidth / 2,
                y: theoreticalY - fretHeight / 2,
              },
              {
                x: theoreticalX + fretWidth / 2,
                y: theoreticalY + fretHeight / 2,
              },
              {
                x: theoreticalX - fretWidth / 2,
                y: theoreticalY + fretHeight / 2,
              },
            ],
            confidence: 0.8, // High confidence for estimated frets
            class: `estimated_${fretNum}`,
          },
          true
        ); // Mark as estimated

        estimatedFrets.push(estimatedFret);
      }
    }

    console.log(
      `Gap filling: Created ${estimatedFrets.length} estimated frets`
    );
    return estimatedFrets;
  }

  /**
   * Estimate scale length and nut position from detected frets
   */
  private estimateScaleLengthAndNut(sortedFrets: Fret[]): {
    scaleLength: number;
    nutX: number;
    nutY: number;
  } {
    if (sortedFrets.length < 2) {
      return { scaleLength: 0, nutX: 0, nutY: 0 };
    }

    // Use the first two frets to estimate scale length
    const fret1 = sortedFrets[0];
    const fret2 = sortedFrets[1];

    const distance = fret2.centroid.x - fret1.centroid.x;

    // Assume these are consecutive frets for now (we could make this smarter)
    // For consecutive frets, the ratio should be approximately 1.0595
    const fretRatio = 1.0594630943592953; // 12th root of 2

    // Estimate scale length based on the distance between frets
    // This is a simplified approach - in reality we'd need to solve the logarithmic equation
    const estimatedScaleLength = distance * 17.817; // Approximate multiplier for typical guitar

    // Estimate nut position (0th fret)
    const nutX =
      fret1.centroid.x - estimatedScaleLength * (1 - Math.pow(1 / 2, 1 / 12));
    const nutY = (fret1.centroid.y + fret2.centroid.y) / 2; // Average Y position

    return {
      scaleLength: estimatedScaleLength,
      nutX,
      nutY,
    };
  }

  /**
   * Get all frets including estimated ones
   */
  getAllFrets(
    includeEstimated: boolean = true,
    maxEstimated: number = 5
  ): Fret[] {
    if (!includeEstimated) {
      return this.frets;
    }

    const estimatedFrets = this.estimateMissingFrets(maxEstimated);
    return [...this.frets, ...estimatedFrets];
  }

  drawFretCentroidsLinearRegression(
    ctx: CanvasRenderingContext2D,
    settings: DrawSettings & {
      length?: number;
      confidenceThreshold?: number;
    } = {}
  ) {
    const length = settings.length || 300;
    const threshold = settings.confidenceThreshold || 0.3;

    this.frets
      .filter((fret) => fret.confidence >= threshold)
      .forEach((fret) => {
        const perpLine = this.calculatePerpendicularLine(fret);
        const centerX = perpLine.centerX;
        const centerY = perpLine.centerY;

        // Calculate start and end points of perpendicular line
        const halfLength = length / 2;
        let startX, startY, endX, endY;

        if (Math.abs(perpLine.slope) > 1000) {
          // Nearly vertical line
          startX = centerX;
          startY = centerY - halfLength;
          endX = centerX;
          endY = centerY + halfLength;
        } else {
          // Calculate points along the perpendicular line
          const dx =
            halfLength / Math.sqrt(1 + perpLine.slope * perpLine.slope);
          startX = centerX - dx;
          startY = perpLine.slope * startX + perpLine.intercept;
          endX = centerX + dx;
          endY = perpLine.slope * endX + perpLine.intercept;
        }

        ctx.save();
        ctx.strokeStyle = settings.color || 'red';
        ctx.lineWidth = settings.thickness || 1;
        ctx.globalAlpha = settings.opacity || 0.5;

        if (settings.style === 'dashed') ctx.setLineDash([5, 5]);
        else if (settings.style === 'dotted') ctx.setLineDash([2, 2]);
        else ctx.setLineDash([]);

        ctx.beginPath();
        ctx.moveTo(startX, startY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.restore();
      });
  }
}
